import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useBackend } from '../backend/BackendContext';
import { CellarSelector } from '../components/CellarSelector';
import { InlineEditableField } from '../components/InlineEditableField';
import { useCellarList } from '../auth/CellarListContext';
import { DeleteCellarControl } from '../components/DeleteCellarControl';
import { ExportCellarButton } from '../components/ExportCellarButton';
import { TopBar } from '../components/TopBar';
import { useEffect, useState } from 'react';
import { t } from '../i18n';
import { useLanguage } from '../i18n/LanguageProvider';
import type { Language } from '../domain/profile';
import { deleteCellarContents } from '../store/delete-cellar';
import { loadCellarForExport } from '../store/export-cellar';
import { readProfile, saveCellarName, saveDigestEnabled } from '../store/profile-store';
import { cellarName, profileDisplayName } from '../domain/profile';
import './SettingsPage.css';

/**
 * /reglages (contracts/ui-and-emails.md). For everyone: sign out (FR-028) and delete the contents
 * of one's own cellar (FR-039, T074). For the owner, also the cellar switcher (FR-040) and the
 * link to the app's users (FR-044), moved here from the top bar at the owner's request
 * (2026-09-28), the language, French or English (T076d), and "Exporter ma cave en CSV" (T097):
 * two taps from anywhere (SC-016). The owner exports another person's cellar from that cellar's
 * page. The monthly email on/off switch (T111, FR-034) saves `profile.json`'s `digestEnabled`.
 * At the top, the cellar's name (owner request, 2026-10-08), shown in the top bar and the owner's
 * "Changer de cave" list; "Gérer les utilisateurs et leurs caves" is a plain button for the owner.
 */
export function SettingsPage() {
  const { person, signOut } = useAuth();
  const { storage } = useBackend();
  const { language, changeLanguage } = useLanguage();
  const [languageFailed, setLanguageFailed] = useState(false);
  // The monthly email switch (T111): unknown until the profile is read.
  const [digestEnabled, setDigestEnabled] = useState<boolean | undefined>();
  const [digestFailed, setDigestFailed] = useState(false);
  // The cellar's name: undefined until the profile is read, '' when it has none.
  const [name, setName] = useState<string | undefined>();
  const { refresh } = useCellarList();

  useEffect(() => {
    if (!person) return;
    let cancelled = false;
    readProfile(storage, person.id)
      .then((profile) => {
        if (cancelled) return;
        setDigestEnabled(profile?.digestEnabled ?? true);
        setName(profile ? (cellarName(profile) ?? '') : '');
      })
      // An unreadable profile leaves the switch unavailable rather than guessing.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [storage, person]);

  if (!person) return null;

  function pickDigest(next: boolean) {
    const previous = digestEnabled;
    setDigestFailed(false);
    setDigestEnabled(next);
    saveDigestEnabled(storage, person!.id, person!.email, next).catch(() => {
      setDigestEnabled(previous);
      setDigestFailed(true);
    });
  }

  function pickLanguage(next: Language) {
    setLanguageFailed(false);
    changeLanguage(next).catch(() => setLanguageFailed(true));
  }

  return (
    <>
      <TopBar />
      <main className="settings">
        <h1>{t('settings.title')}</h1>
        <p>{person.email}</p>

        {name !== undefined && (
          <section className="settings__section settings__cellar-name">
            <InlineEditableField
              label={t('settings.cellarName')}
              display={name || t('cellar.mine')}
              initial={name}
              onSave={async (draft) => {
                await saveCellarName(storage, person.id, person.email, draft);
                setName(draft.trim().replace(/\s+/g, ' '));
                refresh();
              }}
            />
          </section>
        )}

        <fieldset className="settings__section settings__language">
          <legend>{t('settings.language')}</legend>
          {(['fr', 'en'] as const).map((code) => (
            <label key={code} className="settings__language-option">
              <input
                type="radio"
                name="language"
                value={code}
                checked={language === code}
                onChange={() => pickLanguage(code)}
              />
              {t(code === 'fr' ? 'language.fr' : 'language.en')}
            </label>
          ))}
          {languageFailed && <p role="alert">{t('settings.languageFailed')}</p>}
        </fieldset>

        <fieldset className="settings__section settings__digest">
          <legend>{t('settings.digest')}</legend>
          <label className="settings__language-option">
            <input
              type="checkbox"
              checked={digestEnabled ?? false}
              disabled={digestEnabled === undefined}
              onChange={(event) => pickDigest(event.target.checked)}
            />
            {t('settings.digestLabel')}
          </label>
          {digestFailed && <p role="alert">{t('settings.digestFailed')}</p>}
        </fieldset>

        {person.role === 'owner' && (
          <>
            <section className="settings__section" aria-labelledby="switch-cellar">
              <h2 id="switch-cellar">{t('cellar.switchTitle')}</h2>
              <CellarSelector labelledBy="switch-cellar" />
            </section>
            {/* User management, for the owner only: one button (owner, 2026-10-08). */}
            <section className="settings__section">
              {/* No "›" here (owner, 2026-10-15), like the page's other buttons. */}
              <Link to="/reglages/admin" className="cta-nav cta-nav--plain">
                {t('cellar.manageUsers')}
              </Link>
            </section>
          </>
        )}

        <section className="settings__section">
          <ExportCellarButton
            label={t('export.own')}
            load={async () => {
              const [cellar, profile] = await Promise.all([
                loadCellarForExport(storage, person.id),
                readProfile(storage, person.id).catch(() => undefined),
              ]);
              return { cellar, name: profile ? profileDisplayName(profile) : person.email };
            }}
          />
        </section>

        <section className="settings__section">
          <button type="button" onClick={() => void signOut()}>
            {t('common.signOut')}
          </button>
          <DeleteCellarControl onDelete={() => deleteCellarContents(storage, person.id)} />
        </section>
      </main>
    </>
  );
}
